import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Pause, Play } from 'lucide-react';
import { artistInfo, homepageSelectPaintings } from '../data/mockData';
import { parseSizeDimensions } from '../components/OrientedImage';
import { useLightbox } from '../hooks/useLightbox';
import { LightboxOverlay } from '../components/LightboxOverlay';

const BioSection = () => (
  <div className="max-w-4xl mx-auto px-6 md:px-8 py-20 md:py-28">
    <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
      <div className="md:w-1/2 flex-shrink-0">
        <img
          src={artistInfo.photo}
          alt={artistInfo.fullName}
          className="w-full object-contain rounded shadow-lg"
          data-testid="about-portrait"
        />
      </div>
      <div className="md:w-1/2">
        <h2 className="text-3xl md:text-4xl font-signature text-gray-900 mb-1">
          About Nikhita Kapur
        </h2>
        <p className="text-base font-signature text-teal-600 mb-4">
          painting as niki
        </p>
        <div className="w-12 h-0.5 bg-teal-600 mb-6" />
        <div className="space-y-4">
          {artistInfo.bio.split('\n\n').map((para, i) => (
            <p key={`bio-${i}`} className="text-sm md:text-base font-signature text-gray-700 leading-relaxed">
              {para}
            </p>
          ))}
        </div>
        <Link
          to="/about"
          className="inline-block mt-6 text-sm font-signature text-teal-600 hover:text-teal-800 underline underline-offset-4"
        >
          Full bio & exhibitions
        </Link>
      </div>
    </div>
  </div>
);

const SeriesLinks = () => (
  <div className="text-center mt-10">
    <p className="text-sm font-signature text-gray-400 mb-4">Explore the series</p>
    <div className="flex justify-center gap-8 flex-wrap">
      <Link to="/work/architecture-of-panic" className="text-xs font-signature text-teal-600 hover:text-teal-800 underline underline-offset-4" data-testid="link-aop">
        Architecture of Panic
      </Link>
      <Link to="/work/memory-in-transit" className="text-xs font-signature text-teal-600 hover:text-teal-800 underline underline-offset-4" data-testid="link-mit">
        Memory in Transit
      </Link>
      <Link to="/work/calm-in-chaos" className="text-xs font-signature text-teal-600 hover:text-teal-800 underline underline-offset-4" data-testid="link-cic">
        Calm in Chaos
      </Link>
    </div>
  </div>
);

const Home = () => {
  const selectPaintings = homepageSelectPaintings;
  const [slide, setSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [tick, setTick] = useState(0);
  const [spotlightIndex, setSpotlightIndex] = useState(null);

  const lb = useLightbox();

  useEffect(() => {
    if (isPaused || spotlightIndex !== null) return;
    const timer = setInterval(() => {
      setSlide(prev => (prev + 1) % selectPaintings.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [selectPaintings.length, isPaused, tick, spotlightIndex]);

  const closeSpotlight = useCallback(() => {
    setSpotlightIndex(null);
    lb.resetZoom();
  }, [lb]);

  const goToPrev = useCallback(() => {
    setSpotlightIndex(prev => (prev === 0 ? selectPaintings.length - 1 : prev - 1));
    lb.resetZoom();
  }, [selectPaintings.length, lb]);

  const goToNext = useCallback(() => {
    setSpotlightIndex(prev => (prev === selectPaintings.length - 1 ? 0 : prev + 1));
    lb.resetZoom();
  }, [selectPaintings.length, lb]);

  useEffect(() => {
    const onKey = (e) => {
      if (spotlightIndex === null) return;
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') closeSpotlight();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [spotlightIndex, goToPrev, goToNext, closeSpotlight]);

  const handleOverlayClick = () => {
    if (lb.shouldSuppressClick()) return;
    closeSpotlight();
  };

  const spotlightArt = spotlightIndex !== null ? selectPaintings[spotlightIndex] : null;

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero */}
      <div className="relative w-full h-screen">
        <img
          src="https://customer-assets-39nsmqrw.emergentagent.net/job_aeb4750a-f660-4106-8b4a-114b09057700/artifacts/ua4tlcgu_hero.webp"
          alt="Nikhita Kapur in studio with paintings"
          className="w-full h-full object-cover object-center"
          style={{ objectPosition: '50% 35%' }}
          data-testid="hero-photo"
        />
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
          <ChevronDown className="w-6 h-6 text-gray-400 animate-bounce" />
        </div>
      </div>

      <BioSection />

      {/* Select Paintings Carousel */}
      <div className="pb-20 md:pb-28">
        <h2 className="text-3xl md:text-4xl font-signature text-gray-900 mb-10 text-center">Select Paintings</h2>
        <div className="relative w-full overflow-hidden group" style={{ height: '65vh' }}>
          <div className="flex h-full transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${slide * 100}%)` }}>
            {selectPaintings.map((painting, idx) => (
              <div key={painting.id} className="flex-shrink-0 w-full h-full flex flex-col items-center justify-center px-8" data-testid={`select-slide-${painting.id}`}>
                <div
                  className="relative overflow-hidden cursor-pointer"
                  style={{ height: 'calc(100% - 4rem)', maxWidth: '90%' }}
                  onClick={() => { setSpotlightIndex(idx); setIsPaused(true); }}
                  data-testid={`select-painting-${painting.id}`}
                >
                  <img src={painting.url} alt={painting.name} className="h-full w-auto object-contain" loading="lazy" style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.08))' }} />
                </div>
                <div className="mt-2 text-center">
                  <p className="text-sm font-signature text-gray-700" data-testid={`select-caption-${painting.id}`}>{painting.name}</p>
                  <p className="text-xs font-signature text-gray-400" data-testid={`select-size-${painting.id}`}>{painting.title}, {painting.size}</p>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => { setSlide(prev => (prev === 0 ? selectPaintings.length - 1 : prev - 1)); setTick(t => t + 1); }} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10" data-testid="select-prev-btn" aria-label="Previous painting">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => { setSlide(prev => (prev + 1) % selectPaintings.length); setTick(t => t + 1); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10" data-testid="select-next-btn" aria-label="Next painting">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-2 right-4 flex items-center gap-2 z-10">
            <button onClick={() => setIsPaused(prev => !prev)} className="text-gray-400 hover:text-gray-700 bg-white/80 hover:bg-white p-2 rounded-full transition-all duration-300" aria-label={isPaused ? 'Play carousel' : 'Pause carousel'} data-testid="select-pause-btn">
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <span className="text-gray-400 font-signature text-xs bg-white/80 px-2 py-1 rounded-full" data-testid="select-counter">{slide + 1} / {selectPaintings.length}</span>
          </div>
        </div>
        <SeriesLinks />
      </div>

      {/* Spotlight Lightbox */}
      {spotlightArt && (() => {
        const { w: lbW, h: lbH } = parseSizeDimensions(spotlightArt.size);
        return (
          <LightboxOverlay
            onClose={closeSpotlight}
            onPrev={goToPrev}
            onNext={goToNext}
            currentIndex={spotlightIndex}
            total={selectPaintings.length}
            zoomLevel={lb.zoomLevel}
            isDragging={lb.isDragging}
            onMouseMove={lb.handleMouseMove}
            onMouseUp={lb.handleMouseUp}
            onMouseLeave={lb.handleMouseUp}
            onOverlayClick={handleOverlayClick}
            testIdPrefix="spotlight"
          >
            <div className="max-w-6xl w-full px-4 pb-16 overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-center" style={{ cursor: lb.getCursor() }} onMouseDown={lb.handleMouseDown}>
                <div
                  className="relative"
                  style={{
                    width: `min(100%, calc(80vh * ${lbW / lbH}))`,
                    maxHeight: '80vh',
                    aspectRatio: `${lbW}/${lbH}`,
                    overflow: 'hidden',
                    transform: lb.getTransform(),
                    transition: lb.isDragging ? 'none' : 'transform 0.3s ease',
                  }}
                  data-testid="spotlight-image"
                >
                  <img src={spotlightArt.url} alt={spotlightArt.name} className="w-full h-full object-cover select-none" draggable={false} />
                </div>
              </div>
              <div className="mt-3 text-center">
                <h3 className="text-lg font-signature text-gray-900 mb-0.5">{spotlightArt.name}</h3>
                <p className="text-sm font-signature text-gray-500">{spotlightArt.title}, {spotlightArt.size}, {spotlightArt.year}</p>
              </div>
            </div>
          </LightboxOverlay>
        );
      })()}
    </div>
  );
};

export default Home;
